import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export const INTERVIEW_FIELDS = [
  ["attitude", "Attitude"],
  ["motivation", "Motivation"],
  ["reliability", "Reliability"],
  ["communication", "Communication"],
  ["willingness_to_learn", "Willingness to learn"],
  ["accepts_correction", "Accepts correction"],
  ["safety_mindset", "Safety mindset"],
  ["teamwork", "Teamwork"],
  ["career_commitment", "Career commitment"],
  ["travel_practicality", "Travel / work practicality"],
];

export const INTERVIEW_PROMPTS = [
  "Why do you want to become a plant operator?",
  "What do you think the job involves?",
  "Tell us about a time you had to learn something new.",
  "How do you react if someone corrects you?",
  "If you were unsure about an instruction on site, what would you do?",
  "Are you comfortable with early starts, travel and working outdoors?",
  "What does working safely mean to you?",
];

export const RED_FLAGS = [
  ["unsafe_attitude", "Unsafe attitude"],
  ["poor_reliability", "Poor reliability"],
  ["unwilling_to_accept_instruction", "Unwilling to accept instruction"],
  ["poor_communication", "Poor communication"],
  ["unrealistic_travel", "Unrealistic travel arrangements"],
  ["unsuitable_expectations", "Unsuitable expectations of the role"],
  ["other", "Other"],
];

export function ScorePicker({ value, onChange, testid }) {
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`h-11 w-11 sm:h-9 sm:w-9 rounded-md border font-mono font-bold text-sm transition-colors ${
            value === n ? "bg-lomond text-black border-lomond" : "border-steel text-slate-500 hover:border-lomond bg-white"
          }`}
          data-testid={`${testid}-${n}`}>
          {n}
        </button>
      ))}
    </div>
  );
}

function ScoreGrid({ fields, scores, setScores, prefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
      {fields.map(([k, label]) => (
        <div key={k} className="flex sm:items-center justify-between gap-3 flex-col sm:flex-row bg-elevated rounded-md p-3">
          <span className="text-sm text-charcoal">{label}</span>
          <ScorePicker value={scores[k]} onChange={(v) => setScores({ ...scores, [k]: v })} testid={`${prefix}-${k}`} />
        </div>
      ))}
    </div>
  );
}

const inputCls = "bg-white border-steel text-charcoal h-11";

export const today = () => new Date().toISOString().slice(0, 10);

export function InterviewForm({ value, onChange, onSave, saving }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const flags = value.red_flags || [];
  return (
    <div className="space-y-5" data-testid="interview-form">
      <div className="bg-elevated border border-steel rounded-md p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">Structured prompts</p>
        <ol className="space-y-1.5">
          {INTERVIEW_PROMPTS.map((p, i) => (
            <li key={i} className="text-sm text-slate-600 flex gap-2">
              <span className="font-mono text-xs text-slate-400 shrink-0">{i + 1}.</span>{p}
            </li>
          ))}
        </ol>
        <p className="text-xs text-slate-400 mt-3">Testing: attitude, motivation, reliability, communication, safety mindset, ability to learn.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Date</Label>
          <Input type="date" value={value.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="int-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Assessor</Label>
          <Input value={value.assessor} onChange={(e) => set("assessor", e.target.value)} className={inputCls} data-testid="int-assessor" />
        </div>
      </div>

      <ScoreGrid fields={INTERVIEW_FIELDS} scores={value.scores || {}} setScores={(s) => set("scores", s)} prefix="int" />

      <div className="bg-red-50 border border-red-200 rounded-md p-4 space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-600">Red flags (optional) — triggers management review</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {RED_FLAGS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2.5 text-sm text-charcoal cursor-pointer" data-testid={`redflag-${k}`}>
              <Checkbox checked={flags.includes(k)}
                onCheckedChange={(c) => set("red_flags", c ? [...flags, k] : flags.filter((f) => f !== k))} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-slate-600 text-sm">Overall comments</Label>
        <Textarea value={value.comments} onChange={(e) => set("comments", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="int-comments" />
      </div>

      <Button onClick={onSave} disabled={saving} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="int-save-btn">
        {saving ? "Saving..." : "Save Interview"}
      </Button>
    </div>
  );
}

export const SIMULATOR_FIELDS = [
  ["coordination", "Coordination"],
  ["spatial_awareness", "Spatial awareness"],
  ["control_accuracy", "Control accuracy"],
  ["instruction_following", "Instruction following"],
  ["concentration", "Concentration"],
  ["learning_rate", "Learning rate"],
  ["response_to_correction", "Response to correction"],
  ["safety_behaviour", "Safety behaviour"],
  ["confidence_without_rushing", "Confidence without rushing"],
];

export const SIM_RATINGS = [
  ["strong", "Strong"],
  ["suitable", "Suitable"],
  ["requires_review", "Requires review"],
  ["not_suitable", "Not suitable"],
];

export function SimulatorForm({ value, onChange, onSave, saving, onUploadReport, reportFile, setReportFile }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-5" data-testid="simulator-form">
      <div className="bg-elevated border border-steel rounded-md p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">Simulator Aptitude &amp; Control Assessment</p>
        <p className="text-sm text-slate-600 leading-relaxed">
          This assesses <strong className="text-charcoal">aptitude</strong> — the candidate is not expected to know plant.
          Can this person learn? Can they follow instructions? Do they improve? Do they operate carefully? Do they think safely?
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Date</Label>
          <Input type="date" value={value.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="sim-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Assessor</Label>
          <Input value={value.assessor} onChange={(e) => set("assessor", e.target.value)} className={inputCls} data-testid="sim-assessor" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Simulator / location</Label>
          <Input value={value.simulator_location} onChange={(e) => set("simulator_location", e.target.value)} className={inputCls} data-testid="sim-location" />
        </div>
      </div>

      <ScoreGrid fields={SIMULATOR_FIELDS} scores={value} setScores={(s) => onChange({ ...value, ...s })} prefix="sim" />

      <div className="space-y-1.5">
        <Label className="text-slate-600 text-sm">Assessor comments</Label>
        <Textarea value={value.comments} onChange={(e) => set("comments", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="sim-comments" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-slate-600 text-sm">Final simulator rating</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="sim-rating">
          {SIM_RATINGS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("recommendation", v)}
              className={`px-3 py-2.5 rounded-md border text-xs font-bold uppercase tracking-wide transition-all ${
                value.recommendation === v
                  ? v === "not_suitable" ? "bg-red-600 text-white border-red-600"
                    : v === "requires_review" ? "bg-amber-400 text-black border-amber-400"
                    : "bg-lomond text-black border-lomond"
                  : "bg-white text-slate-600 border-steel hover:border-lomond"
              }`}
              data-testid={`sim-rating-${v}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {onUploadReport && (
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Scored simulator report (upload)</Label>
          <div className="flex gap-2">
            <Input type="file" onChange={(e) => setReportFile(e.target.files?.[0] || null)} className="bg-white border-steel text-charcoal" data-testid="sim-report-upload" />
            <Button type="button" variant="outline" onClick={onUploadReport} className="border-steel text-charcoal bg-white shrink-0" data-testid="sim-report-upload-btn">Upload</Button>
          </div>
          {value.report_url && <a href={value.report_url} target="_blank" rel="noreferrer" className="text-xs text-lomond underline" data-testid="sim-report-current">Current report</a>}
        </div>
      )}

      <Button onClick={onSave} disabled={saving} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="sim-save-btn">
        {saving ? "Saving..." : "Save Simulator Assessment"}
      </Button>
    </div>
  );
}

export const PRACTICAL_FIELDS = [
  ["listens_to_instructions", "Listens to instructions"],
  ["remembers_instructions", "Remembers instructions"],
  ["coordination", "Coordination"],
  ["smoothness", "Smoothness"],
  ["spatial_awareness", "Spatial awareness"],
  ["awareness_of_surroundings", "Awareness of surroundings"],
  ["control_confidence", "Control confidence"],
  ["does_not_rush", "Does not rush"],
  ["stops_when_uncertain", "Stops when uncertain"],
  ["responds_to_correction", "Responds positively to correction"],
  ["machine_sympathy", "Machine sympathy"],
  ["improvement_during_session", "Improvement during session"],
  ["general_attitude", "General attitude"],
  ["overall_aptitude", "Overall aptitude"],
];

export const PRACTICAL_DECISIONS = [
  ["proceed", "Proceed — good potential"],
  ["further_assessment", "Further assessment needed"],
  ["not_proceed", "Do not proceed"],
];

export const PRAC_DEFAULT = {
  date: today(), assessor: "", scores: {},
  starting_ability: "", ability_after_instruction: "", speed_of_improvement: "",
  learning_comments: "", comments: "", decision: "further_assessment",
};

export function PracticalForm({ value, onChange, onSave, saving }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-5" data-testid="practical-form">
      <div className="bg-elevated border border-steel rounded-md p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">Pre-Trainee Practical Aptitude — Assessment Day</p>
        <p className="text-sm text-slate-600 leading-relaxed">
          This assesses <strong className="text-charcoal">raw potential, not competence</strong>. The candidate is inexperienced
          and is not expected to operate to a trained standard. Do they listen? Can they coordinate? Do they stop when unsure?
          This helps decide whether to invest in them. The Day 10 Lomond Foundation Practical is a separate, post-training assessment.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Date</Label>
          <Input type="date" value={value.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="prac-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Assessor</Label>
          <Input value={value.assessor} onChange={(e) => set("assessor", e.target.value)} className={inputCls} data-testid="prac-assessor" />
        </div>
      </div>

      <ScoreGrid fields={PRACTICAL_FIELDS} scores={value.scores || {}} setScores={(s) => set("scores", s)} prefix="prac" />

      <div className="bg-elevated border border-steel rounded-md p-4 space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond">Learning rate</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Starting ability</Label>
            <Input value={value.starting_ability} onChange={(e) => set("starting_ability", e.target.value)} className={inputCls} data-testid="prac-starting" placeholder="e.g. Novice — no machine time" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Ability after instruction</Label>
            <Input value={value.ability_after_instruction} onChange={(e) => set("ability_after_instruction", e.target.value)} className={inputCls} data-testid="prac-after" placeholder="e.g. Basic control within 45 min" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-sm">Speed of improvement</Label>
            <div className="flex gap-1.5" data-testid="prac-speed">
              {["Slow", "Steady", "Fast"].map((s) => (
                <button key={s} type="button" onClick={() => set("speed_of_improvement", s)}
                  className={`flex-1 px-2 py-2.5 rounded-md border text-xs font-bold uppercase tracking-wide transition-colors ${
                    value.speed_of_improvement === s ? "bg-lomond text-black border-lomond" : "border-steel text-slate-500 hover:border-lomond bg-white"
                  }`} data-testid={`prac-speed-${s.toLowerCase()}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-600 text-sm">Learning comments</Label>
          <Textarea value={value.learning_comments} onChange={(e) => set("learning_comments", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="prac-learning-comments" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-slate-600 text-sm">Overall comments</Label>
        <Textarea value={value.comments} onChange={(e) => set("comments", e.target.value)} className="bg-white border-steel text-charcoal" data-testid="prac-comments" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-slate-600 text-sm">Aptitude decision</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="prac-decision">
          {PRACTICAL_DECISIONS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("decision", v)}
              className={`px-3 py-2.5 rounded-md border text-xs font-bold uppercase tracking-wide transition-all ${
                value.decision === v
                  ? v === "not_proceed" ? "bg-red-600 text-white border-red-600"
                    : v === "further_assessment" ? "bg-amber-400 text-black border-amber-400"
                    : "bg-lomond text-black border-lomond"
                  : "bg-white text-slate-600 border-steel hover:border-lomond"
              }`} data-testid={`prac-decision-${v}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <Button onClick={onSave} disabled={saving} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="prac-save-btn">
        {saving ? "Saving..." : "Save Practical Aptitude"}
      </Button>
    </div>
  );
}

